import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Checkbox } from '../catalyst/checkbox'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { issueFor, useCanadaGapFarmStatus, useEntryCheck } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import { useShipments, type ShipmentInput } from '../lib/farm-level-logs'
import { byDateDesc, FarmLogLayout, orNull, text, useSavedEntries, type DialogState } from '../lib/farm-log-layout'
import type { Shipment, ShipmentCorrectiveAction } from '../lib/types'
import { checkBadgeProps, EntryCheckPanel, InputWarning } from './entry-check'
import { EntryIssues } from './entry-issues'

const FORM_NAME = 'Transporting Product'

const CORRECTIVE_ACTIONS: { value: ShipmentCorrectiveAction; label: string }[] = [
  { value: 'refused_load', label: 'A - Refuse to load product' },
  { value: 'swept', label: 'B - Sweep' },
  { value: 'rinsed', label: 'C - Rinse' },
  { value: 'maintenance', label: 'D - Maintenance' },
  { value: 'washed', label: 'E - Wash/clean with soap' },
  { value: 'other', label: 'F - Other' },
]

function ShipmentDialog({ onClose, initial, onSave }: { onClose: () => void; initial?: Shipment; onSave: (input: ShipmentInput) => Promise<unknown> }) {
  const [shippedOn, setShippedOn] = useState(initial?.shippedOn ?? '')
  const [productIdentifier, setProductIdentifier] = useState(text(initial?.productIdentifier))
  const [quantity, setQuantity] = useState(initial?.quantity != null ? String(initial.quantity) : '')
  const [quantityUnit, setQuantityUnit] = useState(text(initial?.quantityUnit))
  const [truckId, setTruckId] = useState(text(initial?.truckId))
  const [destination, setDestination] = useState(text(initial?.destination))
  const [loaderName, setLoaderName] = useState(text(initial?.loaderName))
  const [vehicleIssue, setVehicleIssue] = useState(text(initial?.vehicleIssue))
  const [correctiveAction, setCorrectiveAction] = useState<ShipmentCorrectiveAction | ''>(initial?.correctiveAction ?? '')
  // Attestations start unticked, like H1 "label followed".
  const [vehicleInspected, setVehicleInspected] = useState(initial?.vehicleInspected ?? false)
  const [rotatedAppropriately, setRotatedAppropriately] = useState(initial?.rotatedAppropriately ?? false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Everything but the date, as saved; also what the CanadaGAP check runs on.
  const fields = {
    productIdentifier: orNull(productIdentifier),
    quantity: quantity ? Number(quantity) : null,
    quantityUnit: orNull(quantityUnit),
    truckId: orNull(truckId),
    destination: orNull(destination),
    loaderName: orNull(loaderName),
    vehicleIssue: orNull(vehicleIssue),
    correctiveAction: correctiveAction || null,
    vehicleInspected,
    rotatedAppropriately,
  }
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const check = useEntryCheck(org?.id, farmId, { form: 'o', entry: { shippedOn: shippedOn || null, ...fields } })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!shippedOn) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({ shippedOn, ...fields })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save shipment.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{initial ? 'Edit shipment' : 'Record a shipment'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ComplianceBadge framework="CanadaGAP" formCode="O" formName={FORM_NAME} {...checkBadgeProps(check)} />
        </div>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField>
                <Label>Date</Label>
                <Input type="date" value={shippedOn} onChange={(e) => setShippedOn(e.target.value)} required autoFocus />
              </FormField>
              <FormField>
                <Label>Product identifier</Label>
                <Input value={productIdentifier} onChange={(e) => setProductIdentifier(e.target.value)} placeholder="Lot ID, same as on Form P2 / Q" />
                <InputWarning result={check} input="productIdentifier" />
              </FormField>
              <FormField>
                <Label>Quantity shipped</Label>
                <Input type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                <InputWarning result={check} input="quantity" />
              </FormField>
              <FormField>
                <Label>Unit</Label>
                <Input value={quantityUnit} onChange={(e) => setQuantityUnit(e.target.value)} placeholder="e.g. flats, lb" />
              </FormField>
              <FormField>
                <Label>Truck / trailer ID #</Label>
                <Input value={truckId} onChange={(e) => setTruckId(e.target.value)} />
                <InputWarning result={check} input="truckId" />
              </FormField>
              <FormField>
                <Label>Person responsible (loader)</Label>
                <Input value={loaderName} onChange={(e) => setLoaderName(e.target.value)} />
                <InputWarning result={check} input="loaderName" />
              </FormField>
            </div>
            <FormField>
              <Label>Destination and customer</Label>
              <Input value={destination} onChange={(e) => setDestination(e.target.value)} />
              <InputWarning result={check} input="destination" />
            </FormField>
          </FieldGroup>

          <div className="mt-8 space-y-3 rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-950/5 dark:bg-white/5 dark:ring-white/10">
            <p className="text-sm font-medium text-zinc-950 dark:text-white">Vehicle inspection</p>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Check for pests, damage, odours, foreign materials (manure, garbage, glass, oil, chemicals), maintenance needed, and refrigeration leaks.
            </p>
            <label className="flex items-start gap-3">
              <Checkbox checked={vehicleInspected} onChange={setVehicleInspected} className="mt-0.5" />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Vehicle was inspected</span>
            </label>
            <FormField>
              <Label>Hazard found (leave blank if none)</Label>
              <Input value={vehicleIssue} onChange={(e) => setVehicleIssue(e.target.value)} />
            </FormField>
            {vehicleIssue.trim() && (
              <FormField>
                <Label>Corrective action</Label>
                <Select value={correctiveAction} onChange={(e) => setCorrectiveAction(e.target.value as ShipmentCorrectiveAction | '')}>
                  <option value="">Select…</option>
                  {CORRECTIVE_ACTIONS.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}
            <label className="flex items-start gap-3">
              <Checkbox checked={rotatedAppropriately} onChange={setRotatedAppropriately} className="mt-0.5" />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Product is rotated appropriately (shelf life considered)</span>
            </label>
          </div>
          <EntryCheckPanel result={check} formCode="O" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="O" formName={FORM_NAME} placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function ShipmentsPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const { org } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [dialog, setDialog] = useState<DialogState<Shipment>>(null)
  const saved = useSavedEntries(dialog !== null, year)

  const { logs, loading, error, addLog, updateLog } = useShipments(org?.id, farmId, year)
  const { status, reload: reloadStatus } = useCanadaGapFarmStatus(org?.id, farmId, year)

  async function save(input: ShipmentInput) {
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog(input)
    await reloadStatus()
    if (log) saved.setIds([log.id])
  }

  const entries = [...logs].sort(byDateDesc((r) => r.shippedOn)).map((log) => ({
    key: log.id,
    date: log.shippedOn,
    onClick: () => setDialog({ log }),
    content: (
      <>
        <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
          {log.productIdentifier ?? 'Shipment'}
          {log.quantity != null && (
            <span className="text-zinc-400 dark:text-zinc-500">
              {' '}
              · {log.quantity} {log.quantityUnit}
            </span>
          )}
        </p>
        {(log.destination || log.truckId) && (
          <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">{[log.destination, log.truckId].filter(Boolean).join(' · ')}</p>
        )}
        <EntryIssues issue={issueFor(status?.forms.o, log.id)} />
      </>
    ),
  }))

  return (
    <FarmLogLayout
      orgId={org?.id}
      farmId={farmId}
      form="o"
      title="Shipping"
      formName={FORM_NAME}
      addLabel="Record a shipment"
      onAdd={() => setDialog({})}
      year={year}
      onYearChange={setYear}
      status={status}
      loading={loading}
      error={error}
      entries={entries}
      emptyText={`No shipments in ${year}.`}
      saved={saved}
    >
      {dialog && <ShipmentDialog key={dialog.log?.id ?? 'new'} onClose={() => setDialog(null)} initial={dialog.log} onSave={save} />}
    </FarmLogLayout>
  )
}
